import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, ReportActionCreators } from '@nkr/store/index';
import { INcsAuditResponse } from '@nkr/store/reports';
import { INcsAudit } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { IHeaderParams, GridApi, ColumnApi, GridReadyEvent, ValueGetterParams, ColDef, FirstDataRenderedEvent, ICellRendererParams } from 'ag-grid-community';
import { ReportWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getNcsAudit: (centerId: number) => Promise<INcsAuditResponse>
}

interface IProps {
    centerId: number
}

type TProps = IStateProps & IDispatchProps & IProps

interface IState {
    data: INcsAudit[],
    gridApi: GridApi,
    columns: ColDef[]
}

class NcsAuditReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        gridApi: null,
        columns: [
            {
                colId: 'rank',
                headerName: 'Rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as INcsAudit;
                    
                    if (data.centerName == 'TOTALS:') {
                        return null;
                    }

                    return params.value;
                },
                width: 50,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.centerName === 'TOTALS:') {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
            },

            {
                colId: 'centerName',
                field: 'centerName',
                headerName: 'Transplant Center',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
                width: 155
            },

            {
                colId: 'chainNumber',
                field: 'chainNumber',
                headerName: 'Chain Number',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Chain Number</SortableHeaderComponent>,
                width: 105
            },

            {
                colId: 'drAlias',
                field: 'drAlias',
                headerName: 'Donor Recipient Alias',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Recipient Alias</SortableHeaderComponent>,
                width: 155
            },

            {
                colId: 'txpDate',
                field: 'txpDate',
                headerName: 'Transplant Date',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Transplant Date</SortableHeaderComponent>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as INcsAudit;

                    if (data.txpDate != '') {
                        return moment(data.txpDate).format('MM/DD/YYYY');
                    }

                    return null;
                },
                comparator: Utility.dateComparator,
                width: 155
            },

            {
                colId: 'patientAbo',
                field: 'patientAbo',
                headerName: 'Blood Type',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Blood Type</SortableHeaderComponent>,
                width: 125
            },

            {
                colId: 'type',
                field: 'type',
                headerName: 'Patient Type',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Patient Type</SortableHeaderComponent>,
                width: 125
            },

            {
                colId: 'ncsAdjust',
                field: 'ncsAdjust',
                headerName: 'NCS Adjustment',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NCS Adjustment</SortableHeaderComponent>,
                width: 125
            },

            {
                colId: 'ncsAdjustFV',
                field: 'ncsAdjustFV',
                headerName: 'NCS Adjustment FV',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NCS Adjustment FV</SortableHeaderComponent>,
                width: 125
            },
        ]
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData('Getting NCS Audit, one moment please...');
    }

    getData = (message: string) => {
        let { centerId } = this.props;

        this.props.showLoading(message).then(() => {
            this.props.getNcsAudit(centerId).then(response => {
                this.setState({
                    data: response.data.results
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    getTotals = (data: INcsAudit[]): INcsAudit => {
        let totalNcs = _.sumBy(data, item => item.ncsAdjust);
        let totalNcsFV = _.sumBy(data, item => item.ncsAdjustFV);

        return {
            centerName: 'TOTALS:',
            chainNumber: null,
            drAlias: '',
            patientAbo: '',
            txpDate: '',
            type: '',
            ncsAdjust: totalNcs,
            ncsAdjustFV: totalNcsFV
        };
    }

    render = () => {
        const { centerId } = this.props;

        const totals = this.getTotals(this.state.data);

        return (
            <>
                <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 20 }}>NCS Audit</div>

                <ReportWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            resizable: false
                        }}
                        columnDefs={this.state.columns}
                        rowData={this.state.data}
                        headerHeight={30}
                        groupHeaderHeight={30}
                        rowHeight={30}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        noRowsOverlayComponentFramework={() => <div>No NCS Data...</div>}
                        getRowStyle={params => {
                            if (params.data.centerName == 'TOTALS:') {
                                return {
                                    backgroundColor: 'lightblue'
                                }
                            }

                            return null;
                        }}
                        onGridReady={this.onGridReady}
                        onFirstDataRendered={this.onFirstDataRendered}
                        pinnedBottomRowData={[totals]}
                    />
                </ReportWrapper>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { dispatch(AppActionCreators.hideLoading()); },
        getNcsAudit: (centerId) => { return dispatch(ReportActionCreators.getNcsAudit(centerId)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(NcsAuditReport);